import { useParams } from 'react-router-dom';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';
import { buildPath } from '@e-socna/utils';

import { Tabs, TabsStrategy } from '../../components/Tabs';
import {
  PATH_RESOLVABLE_MISSION_TYPE_ADMIN,
  PATH_RESOLVABLE_MISSION_TYPE_ADMIN_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_DEFAULT,
  PATH_RESOLVABLE_MISSION_TYPE_FILES,
  PATH_RESOLVABLE_MISSION_TYPE_FILES_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_GENERAL,
  PATH_RESOLVABLE_MISSION_TYPE_GENERAL_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER,
  PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_DELETE,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_MODIFY,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW_IN_GROUP,
  PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_REORDER,
  PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES,
  PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS_EDIT,
  PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS,
  PATH_RESOLVABLE_MISSION_TYPE_VERSIONS,
} from '../../app/constants/paths.constant';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';
import { useIdParam } from '../../utils/use-id-param.util';

import { Admin } from './Admin';
import { GeneralInfo } from './GeneralInfo';
import { Versions } from './Versions';
import { ServiceUsages } from './ServiceUsages';
import { Files } from './Files';
import { Toolkits } from './Toolkits';
import { Templates } from './Templates';
import { QuotationHeader } from './QuotationHeader';

export const ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment =
  graphql(`
    fragment ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment on ResolvableMissionType {
      _id
      ...ResolvableMissionTypePage_GeneralInfo_ResolvableMissionType_Fragment
      ...ResolvableMissionTypePage_Files_ResolvableMissionType_Fragment
      ...ResolvableMissionTypePage_QuotationHeader_ResolvableMissionType_Fragment
      ...ResolvableMissionTypePage_Templates_ResolvableMissionType_Fragment
      ...ResolvableMissionTypePage_Toolkits_ResolvableMissionType_Fragment
      ...ResolvableMissionTypePage_ServiceUsages_ResolvableMissionType_Fragment
      ...ResolvableMissionTypePage_Admin_ResolvableMissionType_Fragment
    }
  `);

interface Props {
  readonly resolvableMissionType: FragmentType<
    typeof ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment
  >;
}

export const ResolvableMissionTypeTabs = ({
  resolvableMissionType: pResolvableMissionType,
}: Readonly<Props>) => {
  const resolvableMissionType = getFragmentData(
    ResolvableMissionTypePage_Tabs_ResolvableMissionType_Fragment,
    pResolvableMissionType,
  );

  const id = useIdParam();
  const { serviceUsageId, serviceUsagesGroupId } = useParams();

  const { paths, getActiveTab } = useBuildPaths(
    {
      admin: PATH_RESOLVABLE_MISSION_TYPE_ADMIN,
      adminEdit: PATH_RESOLVABLE_MISSION_TYPE_ADMIN_EDIT,
      default: PATH_RESOLVABLE_MISSION_TYPE_DEFAULT,
      generalInfo: PATH_RESOLVABLE_MISSION_TYPE_GENERAL,
      generalInfoEdit: PATH_RESOLVABLE_MISSION_TYPE_GENERAL_EDIT,
      templates: PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES,
      templatesEdit: PATH_RESOLVABLE_MISSION_TYPE_TEMPLATES_EDIT,
      quotationHeader: PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER,
      quotationHeaderEdit: PATH_RESOLVABLE_MISSION_TYPE_QUOTATION_HEADER_EDIT,
      serviceUsages: PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES,
      serviceUsagesEdit: PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT,
      serviceUsagesEditDelete:
        PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_DELETE,
      serviceUsagesEditModify:
        PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_MODIFY,
      serviceUsagesEditNew:
        PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW,
      serviceUsagesEditNewInGroup:
        PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW_IN_GROUP,
      serviceUsagesEditReorder:
        PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_REORDER,
      files: PATH_RESOLVABLE_MISSION_TYPE_FILES,
      filesEdit: PATH_RESOLVABLE_MISSION_TYPE_FILES_EDIT,
      toolkits: PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS,
      toolkitsEdit: PATH_RESOLVABLE_MISSION_TYPE_TOOLKITS_EDIT,
      versions: PATH_RESOLVABLE_MISSION_TYPE_VERSIONS,
    },
    {
      id,
      serviceUsageId,
      serviceUsagesGroupId,
    },
    {
      admin: 'admin',
      adminEdit: 'admin',
      default: 'generalInfo',
      generalInfo: 'generalInfo',
      generalInfoEdit: 'generalInfo',
      templates: 'templates',
      templatesEdit: 'templates',
      quotationHeader: 'quotationHeader',
      quotationHeaderEdit: 'quotationHeader',
      serviceUsages: 'serviceUsages',
      serviceUsagesEdit: 'serviceUsages',
      serviceUsagesEditDelete: 'serviceUsages',
      serviceUsagesEditModify: 'serviceUsages',
      serviceUsagesEditNew: 'serviceUsages',
      serviceUsagesEditNewInGroup: 'serviceUsages',
      serviceUsagesEditReorder: 'serviceUsages',
      files: 'files',
      filesEdit: 'files',
      toolkits: 'toolkits',
      toolkitsEdit: 'toolkits',
      versions: 'versions',
    },
  );

  const serviceUsagesPaths = {
    consult: paths.serviceUsages,
    edit: paths.serviceUsagesEdit,
    editDelete: paths.serviceUsagesEditDelete,
    editModify: paths.serviceUsagesEditModify,
    editNew: paths.serviceUsagesEditNew,
    // Built without the group: each group form fills its own.
    editNewInGroup: buildPath(
      PATH_RESOLVABLE_MISSION_TYPE_SERVICE_USAGES_EDIT_NEW_IN_GROUP,
      { id },
    ),
    editReorder: paths.serviceUsagesEditReorder,
  } as const;

  return (
    <Tabs
      activeTab={getActiveTab()}
      id="resolvable-mission-type-tabs"
      strategy={TabsStrategy.Controlled}
      tabs={[
        {
          key: 'generalInfo',
          label: 'Informations Générales',
          panel: <GeneralInfo resolvableMissionType={resolvableMissionType} />,
          to: paths.generalInfo,
          value: 'generalInfo',
        },
        {
          key: 'templates',
          label: 'Modèles',
          panel: <Templates resolvableMissionType={resolvableMissionType} />,
          to: paths.templates,
          value: 'templates',
        },
        {
          key: 'quotationHeader',
          label: 'Entête de devis',
          panel: (
            <QuotationHeader resolvableMissionType={resolvableMissionType} />
          ),
          to: paths.quotationHeader,
          value: 'quotationHeader',
        },
        {
          key: 'serviceUsages',
          label: 'Prestations',
          panel: (
            <ServiceUsages
              paths={serviceUsagesPaths}
              resolvableMissionType={resolvableMissionType}
            />
          ),
          to: paths.serviceUsages,
          value: 'serviceUsages',
        },
        {
          key: 'files',
          label: 'Fichiers',
          panel: <Files resolvableMissionType={resolvableMissionType} />,
          to: paths.files,
          value: 'files',
        },
        {
          key: 'toolkits',
          label: 'Outils',
          panel: <Toolkits resolvableMissionType={resolvableMissionType} />,
          to: paths.toolkits,
          value: 'toolkits',
        },
        {
          key: 'versions',
          label: 'Versions',
          panel: <Versions originalId={resolvableMissionType._id} />,
          to: paths.versions,
          value: 'versions',
        },
        {
          key: 'admin',
          label: 'Administration',
          panel: <Admin resolvableMissionType={resolvableMissionType} />,
          to: paths.admin,
          value: 'admin',
        },
      ]}
      title="Onglets de l'écran de Fiche d'un Type de Mission"
    />
  );
};
